import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Loading } from "../components/Loader";
import { Select } from "../components/Select";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { getAccessToken } from "../shared/tokenStorage";
import {
  fetchWholesaleSale,
  addWholesaleItem,
  updateWholesaleItem,
  removeWholesaleItem,
  updateWholesaleSaleDiscount,
  confirmWholesaleSale,
  createWholesalePayment,
  searchProductsForWholesale,
  selectCurrentSale,
  selectCurrentSaleStatus,
  selectCurrentSaleError,
  selectProductSearchResults,
  selectProductSearchStatus,
  selectProductSearchError,
  clearProductSearch,
  clearCurrentSale,
} from "../features/wholesale/wholesaleSlice";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "";

const RETAIL_REF_LS_KEY = "auf_wholesale_show_retail_ref";

async function downloadFile(url, filename) {
  const token = getAccessToken();
  const res = await fetch(`${API_BASE}${url}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error("Ошибка экспорта");
  const blob = await res.blob();
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}

/** Draft-only: сервер отдаёт insufficient_stock и store_stock_quantity для склада продажи */
function wholesaleStockWarningText(item, isDraft) {
  if (!isDraft) return null;
  const stock = Number(item.store_stock_quantity ?? 0);
  const need = Number(item.quantity);
  let insufficient;
  if (typeof item.insufficient_stock === "boolean") {
    insufficient = item.insufficient_stock;
  } else {
    insufficient = stock < need;
  }
  if (!insufficient) return null;
  if (stock <= 0) return "Нет на складе этого магазина";
  const needStr = Number.isFinite(need)
    ? need.toFixed(3).replace(/\.?0+$/, "")
    : String(item.quantity);
  return `Недостаточно на складе: доступно ${stock}, в заказе ${needStr}`;
}

export default function WholesaleSalePage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { saleId } = useParams();

  const sale = useSelector(selectCurrentSale);
  const saleStatus = useSelector(selectCurrentSaleStatus);
  const saleError = useSelector(selectCurrentSaleError);
  const searchResults = useSelector(selectProductSearchResults);
  const searchStatus = useSelector(selectProductSearchStatus);
  const searchError = useSelector(selectProductSearchError);

  const [searchQuery, setSearchQuery] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [lastAdded, setLastAdded] = useState(null); // { name, qty, isIncrement }
  const [editingItem, setEditingItem] = useState(null); // { id, quantity, wholesale_price, manual_discount }
  const [itemActionLoading, setItemActionLoading] = useState(null); // itemId being updated/removed
  const [addLoading, setAddLoading] = useState(false);
  const [addError, setAddError] = useState(null);

  const debounceRef = useRef(null);
  const lastAddedTimerRef = useRef(null);

  const [discountType, setDiscountType] = useState("none");
  const [discountValue, setDiscountValue] = useState("");
  const [discountLoading, setDiscountLoading] = useState(false);
  const [discountError, setDiscountError] = useState(null);

  const [confirmModal, setConfirmModal] = useState(false);
  const [paymentMode, setPaymentMode] = useState("paid");
  const [paymentType, setPaymentType] = useState("cash");
  const [initialPayment, setInitialPayment] = useState("");
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [confirmError, setConfirmError] = useState(null);

  const [repayModal, setRepayModal] = useState(false);
  const [repayAmount, setRepayAmount] = useState("");
  const [repayType, setRepayType] = useState("cash");
  const [repayNotes, setRepayNotes] = useState("");
  const [repayLoading, setRepayLoading] = useState(false);
  const [repayError, setRepayError] = useState(null);

  const [exportLoading, setExportLoading] = useState(null);
  const [exportError, setExportError] = useState(null);

  const [showRetailReference, setShowRetailReference] = useState(() => {
    try {
      return typeof window !== "undefined" && window.localStorage.getItem(RETAIL_REF_LS_KEY) === "1";
    } catch {
      return false;
    }
  });

  const searchInputRef = useRef(null);

  const persistRetailRefToggle = useCallback((next) => {
    setShowRetailReference(next);
    try {
      if (typeof window !== "undefined") {
        window.localStorage.setItem(RETAIL_REF_LS_KEY, next ? "1" : "0");
      }
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    dispatch(clearCurrentSale());
    dispatch(fetchWholesaleSale(saleId));
    return () => {
      dispatch(clearProductSearch());
    };
  }, [dispatch, saleId]);

  useEffect(() => {
    if (sale) {
      setDiscountType(sale.order_discount_type || "none");
      setDiscountValue(
        sale.order_discount_type !== "none" ? String(sale.order_discount_value || "0") : ""
      );
    }
  }, [sale?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── POS: debounced search as-you-type ──────────────────────────────────────
  const handleQueryChange = (e) => {
    const val = e.target.value;
    setSearchQuery(val);
    setSelectedIdx(0);
    setAddError(null);

    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (val.trim().length >= 1) {
      debounceRef.current = setTimeout(() => {
        dispatch(searchProductsForWholesale(val.trim())).then(() => setDropdownOpen(true));
      }, 220);
    } else {
      dispatch(clearProductSearch());
      setDropdownOpen(false);
    }
  };

  // ── POS: add or increment product ─────────────────────────────────────────
  const addProductToCart = useCallback(
    async (product) => {
      if (!product) return;
      setAddLoading(true);
      setAddError(null);

      const existingItem = (sale?.items || []).find((i) => i.product_id === product.id);
      const wholesalePrice =
        Number(product.wholesale_price) > 0
          ? Number(product.wholesale_price).toFixed(2)
          : Number(product.cost_price || 0).toFixed(2);

      try {
        if (existingItem) {
          const newQty = String(Number(existingItem.quantity) + 1);
          await dispatch(
            updateWholesaleItem({
              itemId: existingItem.id,
              quantity: newQty,
              wholesale_price: String(existingItem.wholesale_price),
              manual_discount: String(existingItem.manual_discount),
            })
          ).unwrap();
          showToast(product.name, Number(newQty), true);
        } else {
          await dispatch(
            addWholesaleItem({
              saleId,
              product_id: product.id,
              quantity: "1",
              wholesale_price: wholesalePrice,
              manual_discount: "0",
            })
          ).unwrap();
          showToast(product.name, 1, false);
        }

        dispatch(fetchWholesaleSale(saleId));
        dispatch(clearProductSearch());
        setSearchQuery("");
        setDropdownOpen(false);
        setSelectedIdx(0);
        if (searchInputRef.current) searchInputRef.current.focus();
      } catch (err) {
        const msg = typeof err === "string" ? err : err?.detail || "Ошибка добавления";
        setAddError(msg);
      } finally {
        setAddLoading(false);
      }
    },
    [dispatch, sale, saleId]
  );

  const showToast = (name, qty, isIncrement) => {
    if (lastAddedTimerRef.current) clearTimeout(lastAddedTimerRef.current);
    setLastAdded({ name, qty, isIncrement });
    lastAddedTimerRef.current = setTimeout(() => setLastAdded(null), 2200);
  };

  // ── POS: keyboard nav in dropdown ─────────────────────────────────────────
  const handleSearchKeyDown = useCallback(
    (e) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIdx((i) => Math.min(i + 1, searchResults.length - 1));
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIdx((i) => Math.max(i - 1, 0));
        return;
      }
      if (e.key === "Escape") {
        setSearchQuery("");
        dispatch(clearProductSearch());
        setDropdownOpen(false);
        setSelectedIdx(0);
        return;
      }
      if (e.key === "Enter") {
        e.preventDefault();
        if (searchResults.length > 0 && dropdownOpen) {
          addProductToCart(searchResults[selectedIdx] || searchResults[0]);
        } else if (searchQuery.trim()) {
          // Force search then add first result
          if (debounceRef.current) clearTimeout(debounceRef.current);
          dispatch(searchProductsForWholesale(searchQuery.trim())).then((action) => {
            const results = action.payload || [];
            if (results.length > 0) {
              addProductToCart(results[0]);
            } else {
              setDropdownOpen(true);
            }
          });
        }
      }
    },
    [dispatch, searchQuery, searchResults, dropdownOpen, selectedIdx, addProductToCart]
  );

  // ── Quick ±1 qty buttons in items table ───────────────────────────────────
  const handleQuickQtyChange = async (item, delta) => {
    const newQty = Math.max(0.001, Number(item.quantity) + delta);
    setItemActionLoading(item.id);
    try {
      await dispatch(
        updateWholesaleItem({
          itemId: item.id,
          quantity: String(newQty % 1 === 0 ? newQty.toFixed(0) : newQty.toFixed(3)),
          wholesale_price: String(item.wholesale_price),
          manual_discount: String(item.manual_discount),
        })
      ).unwrap();
      dispatch(fetchWholesaleSale(saleId));
    } catch {
      // ignore
    } finally {
      setItemActionLoading(null);
    }
  };

  const handleStartEdit = (item) => {
    setEditingItem({
      id: item.id,
      quantity: String(item.quantity),
      wholesale_price: String(item.wholesale_price),
      manual_discount: String(item.manual_discount),
    });
  };

  const handleSaveEdit = async () => {
    if (!editingItem) return;
    setItemActionLoading(editingItem.id);
    try {
      await dispatch(
        updateWholesaleItem({
          itemId: editingItem.id,
          quantity: editingItem.quantity,
          wholesale_price: editingItem.wholesale_price,
          manual_discount: editingItem.manual_discount,
        })
      ).unwrap();
      dispatch(fetchWholesaleSale(saleId));
      setEditingItem(null);
    } catch (err) {
      // keep editing open so user sees
    } finally {
      setItemActionLoading(null);
    }
  };

  const handleRemoveItem = async (itemId) => {
    setItemActionLoading(itemId);
    try {
      await dispatch(removeWholesaleItem(itemId)).unwrap();
      dispatch(fetchWholesaleSale(saleId));
    } catch {
      // ignore
    } finally {
      setItemActionLoading(null);
    }
  };

  const handleSaveDiscount = async () => {
    setDiscountLoading(true);
    setDiscountError(null);
    try {
      await dispatch(
        updateWholesaleSaleDiscount({
          saleId,
          order_discount_type: discountType,
          order_discount_value: discountType === "none" ? "0" : discountValue || "0",
          notes: sale?.notes || "",
        })
      ).unwrap();
      dispatch(fetchWholesaleSale(saleId));
    } catch (err) {
      const msg = typeof err === "string" ? err : err?.detail || "Ошибка сохранения скидки";
      setDiscountError(msg);
    } finally {
      setDiscountLoading(false);
    }
  };

  const handleConfirm = async () => {
    setConfirmLoading(true);
    setConfirmError(null);
    try {
      await dispatch(
        confirmWholesaleSale({
          saleId,
          payment_mode: paymentMode,
          payment_type: paymentType,
          initial_payment: paymentMode === "paid" ? (sale?.grand_total || "0") : (initialPayment || "0"),
        })
      ).unwrap();
      setConfirmModal(false);
      dispatch(fetchWholesaleSale(saleId));
    } catch (err) {
      const msg = typeof err === "string" ? err : err?.detail || "Ошибка подтверждения";
      setConfirmError(msg);
    } finally {
      setConfirmLoading(false);
    }
  };

  const handleRepay = async () => {
    if (!repayAmount || Number(repayAmount) <= 0) {
      setRepayError("Введите сумму");
      return;
    }
    setRepayLoading(true);
    setRepayError(null);
    try {
      await dispatch(
        createWholesalePayment({
          sale_id: Number(saleId),
          customer_id: sale?.customer_id || sale?.customer?.id,
          amount: repayAmount,
          payment_type: repayType,
          notes: repayNotes,
        })
      ).unwrap();
      setRepayModal(false);
      dispatch(fetchWholesaleSale(saleId));
    } catch (err) {
      const msg = typeof err === "string" ? err : err?.detail || "Ошибка платежа";
      setRepayError(msg);
    } finally {
      setRepayLoading(false);
    }
  };

  const handlePrintPdf = useCallback(() => {
    if (!sale) return;
    const retailMode = showRetailReference;
    const customerNameStr =
      typeof sale.customer === "object"
        ? sale.customer.name
        : `Клиент #${sale.customer_id}`;
    const statusLabels = { draft: "Черновик", confirmed: "Подтверждена", canceled: "Отменена" };
    const payLabels = { unpaid: "Не оплачено", partial: "Частично оплачено", paid: "Оплачено" };
    const fmtDate = (d) => {
      if (!d) return "—";
      const dt = new Date(d);
      return dt.toLocaleString("ru-RU", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
    };
    const fmtNum = (n) => Number(n || 0).toLocaleString("ru-RU", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const saleItems = sale.items || [];
    const payments = sale.payments || [];

    const itemRows = saleItems.map((item, idx) => `
      <tr class="${idx % 2 === 0 ? "row-even" : "row-odd"}">
        <td class="td-num">${idx + 1}</td>
        <td class="td-name">${item.product_name || "—"}</td>
        <td class="td-code">${item.barcode || "—"}</td>
        <td class="td-right">${fmtNum(item.quantity)}</td>
        <td class="td-right">${fmtNum(item.wholesale_price)}</td>
        ${retailMode ? `<td class="td-right">${fmtNum(item.cost_price)}</td>` : ""}
        <td class="td-right">${fmtNum(item.manual_discount)}</td>
        <td class="td-right td-total">${fmtNum(item.line_total)}</td>
        ${retailMode ? `<td class="td-right td-total" style="color:#64748b">${fmtNum(item.retail_line_reference)}</td>` : ""}
      </tr>`).join("");

    const payTypeLabels = { cash: "Наличные", card: "Карта", transfer: "Перевод", none: "—" };
    const paymentRows = payments.length > 0
      ? payments.map((p) => `
        <tr>
          <td>${fmtDate(p.payment_dt)}</td>
          <td>${payTypeLabels[p.payment_type] || p.payment_type || "—"}</td>
          <td class="td-right td-total">${fmtNum(p.amount)} сум</td>
        </tr>`).join("")
      : `<tr><td colspan="3" style="color:#94a3b8;text-align:center;padding:8px">Платежи отсутствуют</td></tr>`;

    const showDiscount = Number(sale.item_discount_total || 0) > 0 || Number(sale.order_discount_total || 0) > 0;

    const html = `<!DOCTYPE html>
<html lang="ru">
<head>
<meta charset="UTF-8">
<title>Оптовая продажа #${sale.id}</title>
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'Segoe UI', Arial, sans-serif; font-size: 11px; color: #1a1a1a; background: #fff; padding: 20px 28px; }
  /* ---- Header ---- */
  .doc-header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid #2563eb; padding-bottom: 12px; margin-bottom: 16px; }
  .doc-header .left .company { font-size: 18px; font-weight: 700; color: #1e40af; letter-spacing: 0.5px; }
  .doc-header .left .subtitle { font-size: 11px; color: #64748b; margin-top: 2px; }
  .doc-header .right { text-align: right; }
  .doc-header .right .doc-title { font-size: 20px; font-weight: 700; color: #1e40af; }
  .doc-header .right .doc-num { font-size: 13px; color: #64748b; margin-top: 2px; }
  /* ---- Info Section ---- */
  .info-section { display: grid; grid-template-columns: 1fr 1fr; gap: 0; border: 1px solid #e2e8f0; border-radius: 4px; margin-bottom: 18px; overflow: hidden; }
  .info-block { padding: 10px 14px; background: #f8fafc; }
  .info-block:nth-child(even) { background: #fff; border-left: 1px solid #e2e8f0; }
  .info-row { display: flex; align-items: baseline; gap: 8px; margin-bottom: 5px; }
  .info-row:last-child { margin-bottom: 0; }
  .info-label { font-size: 10px; color: #64748b; min-width: 110px; text-transform: uppercase; letter-spacing: 0.4px; }
  .info-value { font-size: 11.5px; font-weight: 600; color: #1e293b; }
  .badge { display: inline-block; padding: 1px 8px; border-radius: 10px; font-size: 10px; font-weight: 600; }
  .badge-draft { background: #f1f5f9; color: #64748b; border: 1px solid #cbd5e1; }
  .badge-confirmed { background: #dcfce7; color: #166534; border: 1px solid #86efac; }
  .badge-canceled { background: #fee2e2; color: #991b1b; border: 1px solid #fca5a5; }
  .badge-unpaid { background: #fee2e2; color: #991b1b; border: 1px solid #fca5a5; }
  .badge-partial { background: #fff7ed; color: #9a3412; border: 1px solid #fdba74; }
  .badge-paid { background: #dcfce7; color: #166534; border: 1px solid #86efac; }
  /* ---- Section Title ---- */
  .section-title { font-size: 11px; font-weight: 700; color: #1e40af; text-transform: uppercase; letter-spacing: 0.8px; margin-bottom: 6px; margin-top: 18px; padding-bottom: 4px; border-bottom: 1px solid #bfdbfe; }
  /* ---- Tables ---- */
  table { width: 100%; border-collapse: collapse; font-size: 10.5px; }
  th { background: #1e40af; color: #fff; padding: 6px 8px; text-align: left; font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.4px; white-space: nowrap; }
  td { padding: 5px 8px; border-bottom: 1px solid #e2e8f0; vertical-align: middle; }
  .row-even { background: #fff; }
  .row-odd { background: #f8fafc; }
  .td-right { text-align: right; white-space: nowrap; }
  .td-num { text-align: center; color: #94a3b8; width: 28px; }
  .td-code { color: #64748b; font-family: monospace; font-size: 9.5px; }
  .td-name { font-weight: 500; }
  .td-total { font-weight: 700; color: #1e293b; }
  /* ---- Totals ---- */
  .totals-wrap { display: flex; justify-content: flex-end; margin-top: 10px; }
  .totals-table { min-width: 280px; }
  .totals-table td { border: none; padding: 3px 8px; }
  .totals-table .tot-label { color: #64748b; text-align: right; font-size: 10.5px; }
  .totals-table .tot-val { text-align: right; font-weight: 600; font-size: 11px; min-width: 90px; }
  .totals-table .grand-row td { border-top: 2px solid #1e40af; padding-top: 6px; font-size: 13px; font-weight: 700; color: #1e40af; }
  .totals-table .debt-row td { color: #b91c1c; font-weight: 700; font-size: 12px; }
  /* ---- Footer ---- */
  .doc-footer { margin-top: 28px; border-top: 1px solid #e2e8f0; padding-top: 8px; display: flex; justify-content: space-between; color: #94a3b8; font-size: 9.5px; }
  /* ---- Notes ---- */
  .notes-box { background: #fefce8; border: 1px solid #fef08a; border-radius: 4px; padding: 8px 12px; font-size: 10.5px; color: #713f12; margin-top: 14px; }
  @media print {
    body { padding: 0; }
    @page { margin: 14mm 12mm; }
  }
</style>
</head>
<body>
  <div class="doc-header">
    <div class="left">
      <div class="company">АУФ</div>
      <div class="subtitle">Оптовая торговля</div>
    </div>
    <div class="right">
      <div class="doc-title">Накладная</div>
      <div class="doc-num">№ ${sale.id} от ${fmtDate(sale.sale_dt)}</div>
    </div>
  </div>

  <div class="info-section">
    <div class="info-block">
      <div class="info-row"><span class="info-label">Клиент</span><span class="info-value">${customerNameStr}</span></div>
      <div class="info-row"><span class="info-label">Дата создания</span><span class="info-value">${fmtDate(sale.created_at)}</span></div>
    </div>
    <div class="info-block">
      <div class="info-row"><span class="info-label">Статус</span><span class="info-value"><span class="badge badge-${sale.status || "draft"}">${statusLabels[sale.status] || sale.status}</span></span></div>
      <div class="info-row"><span class="info-label">Оплата</span><span class="info-value"><span class="badge badge-${sale.payment_status || "unpaid"}">${payLabels[sale.payment_status] || sale.payment_status}</span></span></div>
    </div>
  </div>

  <div class="section-title">Товары</div>
  <table>
    <thead>
      <tr>
        <th class="td-num">#</th>
        <th>Наименование</th>
        <th>Штрих-код</th>
        <th class="td-right">Кол-во</th>
        <th class="td-right">Опт</th>
        ${retailMode ? `<th class="td-right">Розница</th>` : ""}
        <th class="td-right">Скидка</th>
        <th class="td-right">Сумма опт</th>
        ${retailMode ? `<th class="td-right">Сумма розн. (справочно)</th>` : ""}
      </tr>
    </thead>
    <tbody>${itemRows || `<tr><td colspan="${retailMode ? 9 : 7}" style="text-align:center;color:#94a3b8;padding:10px">Нет позиций</td></tr>`}</tbody>
  </table>

  <div class="totals-wrap">
    <table class="totals-table">
      <tbody>
        <tr class="debt-row"><td class="tot-label">Предыдущий долг</td><td class="tot-val">${fmtNum(Math.max(0, (Number(sale.customer?.total_debt) || 0) - (Number(sale.debt_amount) || 0)))} сум</td></tr>
        ${showDiscount ? `<tr><td class="tot-label">Подытог</td><td class="tot-val">${fmtNum(sale.subtotal)} сум</td></tr>` : ""}
        ${Number(sale.item_discount_total || 0) > 0 ? `<tr><td class="tot-label">Скидка по позициям</td><td class="tot-val" style="color:#dc2626">− ${fmtNum(sale.item_discount_total)} сум</td></tr>` : ""}
        ${Number(sale.order_discount_total || 0) > 0 ? `<tr><td class="tot-label">Скидка на заказ</td><td class="tot-val" style="color:#dc2626">− ${fmtNum(sale.order_discount_total)} сум</td></tr>` : ""}
        ${retailMode ? `<tr><td class="tot-label" style="color:#64748b">По рознице (справочно, не к оплате)</td><td class="tot-val" style="color:#64748b">${fmtNum(sale.retail_reference_subtotal)} сум</td></tr>` : ""}
        <tr class="grand-row"><td class="tot-label">Итого к оплате</td><td class="tot-val">${fmtNum(sale.grand_total)} сум</td></tr>
        <tr><td class="tot-label">Оплачено</td><td class="tot-val" style="color:#166534">${fmtNum(sale.paid_amount)} сум</td></tr>
        ${Number(sale.debt_amount || 0) > 0 ? `<tr class="debt-row"><td class="tot-label">Долг</td><td class="tot-val">${fmtNum(sale.debt_amount)} сум</td></tr>` : ""}
        <tr class="debt-row"><td class="tot-label">Общий долг клиента</td><td class="tot-val">${fmtNum(sale.customer?.total_debt)} сум</td></tr>
      </tbody>
    </table>
  </div>

  ${payments.length > 0 ? `
  <div class="section-title">Платежи</div>
  <table>
    <thead>
      <tr><th>Дата</th><th>Способ оплаты</th><th class="td-right">Сумма</th></tr>
    </thead>
    <tbody>${paymentRows}</tbody>
  </table>` : ""}

  ${sale.notes ? `<div class="notes-box"><strong>Примечание:</strong> ${sale.notes}</div>` : ""}

  <div class="doc-footer">
    <span>Сформировано: ${new Date().toLocaleString("ru-RU")}</span>
    <span>АУФ — Wholesale Sale #${sale.id}</span>
  </div>

  <script>
    window.onload = function() {
      window.focus();
      window.print();
    };
  </script>
</body>
</html>`;

    const w = window.open("", "_blank", "width=900,height=700");
    if (!w) { alert("Разрешите всплывающие окна для печати PDF"); return; }
    w.document.write(html);
    w.document.close();
  }, [sale, showRetailReference]);

  const handleExport = async (format) => {
    if (format === "pdf") {
      handlePrintPdf();
      return;
    }
    setExportLoading(format);
    setExportError(null);
    try {
      const ext = "xlsx";
      await downloadFile(
        `/api/product/wholesale-sales/${saleId}/export-excel/`,
        `wholesale-sale-${saleId}.${ext}`
      );
    } catch (err) {
      setExportError(err?.message || "Ошибка экспорта");
    } finally {
      setExportLoading(null);
    }
  };

  const isDraft = sale?.status === "draft";
  const isConfirmed = sale?.status === "confirmed";
  const debt = Number(sale?.debt_amount) || 0;
  const customerTotalDebt = Number(sale?.customer?.total_debt) || 0;
  // Долг клиента до этой продажи. Клампим в 0: после погашения общий долг может стать
  // меньше долга накладной, а отрицательный «предыдущий долг» бессмыслен.
  const previousDebt = Math.max(0, customerTotalDebt - debt);
  const items = sale?.items || [];
  const payments = sale?.payments || [];

  const retailReferenceSubtotal = useMemo(() => {
    if (sale?.retail_reference_subtotal != null && sale.retail_reference_subtotal !== "") {
      return Number(sale.retail_reference_subtotal);
    }
    return items.reduce((acc, it) => acc + Number(it.retail_line_reference ?? 0), 0);
  }, [sale?.retail_reference_subtotal, items]);

  const statusLabel = {
    draft: { text: "Черновик", color: "var(--c-text-muted)" },
    confirmed: { text: "Подтверждена", color: "var(--c-success)" },
    canceled: { text: "Отменена", color: "var(--c-danger)" },
  };
  const paymentStatusLabel = {
    unpaid: { text: "Не оплачено", color: "var(--c-danger)" },
    partial: { text: "Частично оплачено", color: "var(--c-accent)" },
    paid: { text: "Оплачено", color: "var(--c-success)" },
  };

  const st = statusLabel[sale?.status] || { text: sale?.status, color: "var(--c-text-muted)" };
  const ps = paymentStatusLabel[sale?.payment_status] || { text: sale?.payment_status, color: "var(--c-text-muted)" };

  const customerName =
    typeof sale?.customer === "object"
      ? sale.customer.name
      : `Клиент #${sale?.customer_id}`;

  return (
    <div style={styles.page}>
      <div style={styles.topBar}>
        <button
          type="button"
          onClick={() =>
            navigate(
              sale?.customer_id || sale?.customer?.id
                ? `/wholesale/${sale?.customer_id || sale?.customer?.id}`
                : "/wholesale"
            )
          }
          style={styles.backBtn}
        >
          ← Назад
        </button>
      </div>

      {saleStatus === "loading" && !sale && <Loading text="Загрузка продажи…" />}
      {saleStatus === "failed" && saleError && <div style={styles.error}>{saleError}</div>}

      {sale && (
        <>
          <div style={styles.saleHeader}>
            <div style={styles.saleHeaderLeft}>
              <div style={styles.saleTitle}>Продажа #{sale.id}</div>
              <div style={styles.saleMeta}>{customerName}</div>
              <div style={styles.saleMeta}>
                {sale.sale_dt ? new Date(sale.sale_dt).toLocaleString("ru-RU") : ""}
              </div>
            </div>
            <div style={styles.saleHeaderRight}>
              <span style={{ ...styles.badge, color: st.color, borderColor: st.color + "55", background: st.color + "18" }}>
                {st.text}
              </span>
              <span style={{ ...styles.badge, color: ps.color, borderColor: ps.color + "55", background: ps.color + "18" }}>
                {ps.text}
              </span>
              <div style={styles.exportRow}>
                <button
                  type="button"
                  style={styles.exportBtn}
                  onClick={() => handleExport("excel")}
                  disabled={exportLoading !== null}
                >
                  {exportLoading === "excel" ? "..." : "↓ Excel"}
                </button>
                <button
                  type="button"
                  style={styles.exportBtn}
                  onClick={handlePrintPdf}
                  disabled={!sale}
                >
                  🖨 PDF
                </button>
              </div>
              {exportError && <div style={styles.errorInline}>{exportError}</div>}
            </div>
          </div>

          {isDraft && (
            <div style={styles.posSection}>
              {/* POS scan bar */}
              <div style={styles.posScanWrap}>
                <span style={styles.posIcon}>⎆</span>
                <div style={styles.posInputWrap}>
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={searchQuery}
                    onChange={handleQueryChange}
                    onKeyDown={handleSearchKeyDown}
                    onFocus={() => searchResults.length > 0 && setDropdownOpen(true)}
                    onBlur={() => setTimeout(() => setDropdownOpen(false), 160)}
                    placeholder="Сканируй штрихкод или введи название… (Enter — добавить)"
                    style={styles.posInput}
                    autoFocus
                    autoComplete="off"
                  />
                  {searchStatus === "loading" && (
                    <span style={styles.posSpinner}>⟳</span>
                  )}

                  {/* Dropdown */}
                  {dropdownOpen && searchResults.length > 0 && (
                    <div style={styles.dropdown}>
                      {searchResults.slice(0, 8).map((p, i) => {
                        const inCart = (sale?.items || []).find((it) => it.product_id === p.id);
                        const isSelected = i === selectedIdx;
                        return (
                          <div
                            key={p.id}
                            style={isSelected ? styles.dropdownItemActive : styles.dropdownItem}
                            onMouseDown={() => addProductToCart(p)}
                          >
                            <div style={styles.dropdownName}>{p.name}</div>
                            <div style={styles.dropdownMeta}>
                              <span style={styles.dropdownBarcode}>{p.barcode || "—"}</span>
                              <span style={styles.dropdownPrice}>
                                опт{" "}
                                {Number(p.wholesale_price) > 0
                                  ? Number(p.wholesale_price).toFixed(2)
                                  : Number(p.cost_price || 0).toFixed(2)}
                                {showRetailReference && (
                                  <span style={styles.dropdownRetailHint}>
                                    {" "}
                                    · розн. {Number(p.cost_price || 0).toFixed(2)}
                                  </span>
                                )}{" "}
                                сом
                              </span>
                              {inCart && (
                                <span style={styles.dropdownInCart}>
                                  ×{Number(inCart.quantity)} в списке
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {dropdownOpen && searchQuery && searchStatus === "succeeded" && searchResults.length === 0 && (
                    <div style={styles.dropdownEmpty}>Товар не найден</div>
                  )}
                </div>

                {/* Toast */}
                {lastAdded && (
                  <div style={styles.addedToast}>
                    <span style={styles.toastIcon}>✓</span>
                    <span>{lastAdded.name}</span>
                    <span style={styles.toastQty}>×{lastAdded.qty}</span>
                    {lastAdded.isIncrement && <span style={styles.toastHint}> +1</span>}
                  </div>
                )}
              </div>

              {(addError || searchError) && (
                <div style={styles.errorInline}>{addError || searchError}</div>
              )}
            </div>
          )}

          <div style={styles.section}>
            <div style={styles.sectionHeading}>
              <div style={styles.sectionTitle}>Товары ({items.length})</div>
              <label style={styles.retailToggle}>
                <input
                  type="checkbox"
                  checked={showRetailReference}
                  onChange={(e) => persistRetailRefToggle(e.target.checked)}
                />
                <span>Показать розничные цены (справочно)</span>
              </label>
            </div>
            {items.length === 0 ? (
              <div style={styles.empty}>Нет товаров. Добавьте товары через поиск выше.</div>
            ) : (
              <div style={styles.tableWrap}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>Товар</th>
                      <th style={styles.th}>Штрихкод</th>
                      {isDraft && <th style={styles.th}>На складе</th>}
                      <th style={styles.th}>Кол-во</th>
                      <th style={styles.th}>Опт. цена</th>
                      {showRetailReference && <th style={styles.th}>Розница</th>}
                      <th style={styles.th}>Скидка</th>
                      <th style={styles.th}>Сумма (опт)</th>
                      {showRetailReference && <th style={styles.th}>Сумма розн.</th>}
                      {isDraft && <th style={styles.th}>Действия</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item) => {
                      const isEditing = editingItem?.id === item.id;
                      const isLoading = itemActionLoading === item.id;
                      const stockWarn = wholesaleStockWarningText(item, isDraft);
                      const rowStyle =
                        stockWarn != null
                          ? {
                              ...styles.trStockWarn,
                              ...(editingItem?.id === item.id ? styles.trStockWarnEditing : {}),
                            }
                          : styles.trStatic;
                      return (
                        <tr key={item.id} style={rowStyle}>
                          <td style={styles.td}>
                            <div style={{ fontWeight: 700 }}>{item.product_name}</div>
                            {stockWarn && (
                              <div style={styles.stockWarnMsg} role="alert">
                                {stockWarn}
                              </div>
                            )}
                          </td>
                          <td style={styles.tdMono}>{item.barcode || "—"}</td>
                          {isDraft && (
                            <td style={styles.tdMono}>
                              {Number(item.store_stock_quantity ?? 0)}
                            </td>
                          )}
                          <td style={styles.td}>
                            {isEditing ? (
                              <input
                                type="number"
                                min="0.001"
                                step="0.001"
                                value={editingItem.quantity}
                                onChange={(e) =>
                                  setEditingItem((ei) => ({ ...ei, quantity: e.target.value }))
                                }
                                style={styles.cellInput}
                              />
                            ) : isDraft ? (
                              <div style={styles.qtyControl}>
                                <button
                                  type="button"
                                  style={styles.qtyBtn}
                                  onClick={() => handleQuickQtyChange(item, -1)}
                                  disabled={isLoading}
                                >−</button>
                                <span style={styles.qtyValue}>
                                  {Number(item.quantity).toFixed(3).replace(/\.?0+$/, "")}
                                </span>
                                <button
                                  type="button"
                                  style={styles.qtyBtn}
                                  onClick={() => handleQuickQtyChange(item, +1)}
                                  disabled={isLoading}
                                >+</button>
                              </div>
                            ) : (
                              Number(item.quantity).toFixed(3).replace(/\.?0+$/, "")
                            )}
                          </td>
                          <td style={styles.td}>
                            {isEditing ? (
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={editingItem.wholesale_price}
                                onChange={(e) =>
                                  setEditingItem((ei) => ({ ...ei, wholesale_price: e.target.value }))
                                }
                                style={styles.cellInput}
                              />
                            ) : (
                              Number(item.wholesale_price).toFixed(2)
                            )}
                          </td>
                          {showRetailReference && (
                            <td style={{ ...styles.td, color: "var(--c-text-muted)" }}>
                              {Number(item.cost_price ?? 0).toFixed(2)}
                            </td>
                          )}
                          <td style={styles.td}>
                            {isEditing ? (
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={editingItem.manual_discount}
                                onChange={(e) =>
                                  setEditingItem((ei) => ({ ...ei, manual_discount: e.target.value }))
                                }
                                style={styles.cellInput}
                              />
                            ) : (
                              Number(item.manual_discount) > 0 ? (
                                <span style={{ color: "var(--c-accent)" }}>−{Number(item.manual_discount).toFixed(2)}</span>
                              ) : "0"
                            )}
                          </td>
                          <td style={styles.td}>
                            <strong>{Number(item.line_total).toFixed(2)}</strong>
                          </td>
                          {showRetailReference && (
                            <td style={{ ...styles.td, color: "var(--c-text-muted)" }}>
                              {Number(item.retail_line_reference ?? 0).toFixed(2)}
                            </td>
                          )}
                          {isDraft && (
                            <td style={styles.td}>
                              <div style={styles.rowActions}>
                                {isEditing ? (
                                  <>
                                    <button
                                      type="button"
                                      style={styles.saveBtn}
                                      onClick={handleSaveEdit}
                                      disabled={isLoading}
                                    >
                                      {isLoading ? "..." : "✓"}
                                    </button>
                                    <button
                                      type="button"
                                      style={styles.cancelSmallBtn}
                                      onClick={() => setEditingItem(null)}
                                      disabled={isLoading}
                                    >
                                      ✕
                                    </button>
                                  </>
                                ) : (
                                  <>
                                    <button
                                      type="button"
                                      style={styles.editBtn}
                                      onClick={() => handleStartEdit(item)}
                                      disabled={isLoading}
                                    >
                                      Изм.
                                    </button>
                                    <button
                                      type="button"
                                      style={styles.removeBtn}
                                      onClick={() => handleRemoveItem(item.id)}
                                      disabled={isLoading}
                                    >
                                      {isLoading ? "..." : "Удалить"}
                                    </button>
                                  </>
                                )}
                              </div>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {isDraft && (
            <div style={styles.discountSection}>
              <div style={styles.sectionTitle}>Общая скидка на заказ</div>
              <div style={styles.discountRow}>
                <Select
                  value={discountType}
                  onChange={(v) => { setDiscountType(v);
                    if (v === "none") setDiscountValue(""); }}
                  options={[
                    { value: "none", label: "Без скидки" },
                    { value: "percent", label: "Процент (%)" },
                    { value: "amount", label: "Фиксированная сумма (сом)" },
                  ]}
                  isSearchable={false}
                />
                {discountType !== "none" && (
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={discountValue}
                    onChange={(e) => setDiscountValue(e.target.value)}
                    placeholder={discountType === "percent" ? "Напр.: 5" : "Напр.: 500"}
                    style={styles.discountInput}
                  />
                )}
                <button
                  type="button"
                  style={styles.saveDiscountBtn}
                  onClick={handleSaveDiscount}
                  disabled={discountLoading}
                >
                  {discountLoading ? "Сохранение..." : "Сохранить скидку"}
                </button>
              </div>
              {discountError && <div style={styles.errorInline}>{discountError}</div>}
            </div>
          )}

          <div style={styles.totalsCard}>
            <div style={styles.totalsGrid}>
              {/* Долг клиента до этой продажи — показываем первым, чтобы итог читался
                  сверху вниз: что было должен, что купил сейчас, сколько должен всего. */}
              <div style={{ ...styles.totalRow, paddingBottom: 8, borderBottom: "1px solid var(--c-border)" }}>
                <span style={styles.totalLabel}>Предыдущий долг</span>
                <span style={{ ...styles.totalVal, color: "var(--c-danger)", fontWeight: 900 }}>
                  {previousDebt.toFixed(2)} сом
                </span>
              </div>
              <div style={styles.totalRow}>
                <span style={styles.totalLabel}>Подытог</span>
                <span style={styles.totalVal}>{Number(sale.subtotal || 0).toFixed(2)} сом</span>
              </div>
              <div style={styles.totalRow}>
                <span style={styles.totalLabel}>Скидка по товарам</span>
                <span style={{ ...styles.totalVal, color: "var(--c-accent)" }}>
                  −{Number(sale.item_discount_total || 0).toFixed(2)} сом
                </span>
              </div>
              {Number(sale.order_discount_total) > 0 && (
                <div style={styles.totalRow}>
                  <span style={styles.totalLabel}>
                    Скидка на заказ
                    {sale.order_discount_type === "percent"
                      ? ` (${Number(sale.order_discount_value).toFixed(0)}%)`
                      : ""}
                  </span>
                  <span style={{ ...styles.totalVal, color: "var(--c-accent)" }}>
                    −{Number(sale.order_discount_total || 0).toFixed(2)} сом
                  </span>
                </div>
              )}
              {showRetailReference && items.length > 0 && (
                <div style={styles.totalRow}>
                  <span style={{ ...styles.totalLabel, opacity: 0.65 }}>
                    По рознице (справочно, не к оплате)
                  </span>
                  <span style={{ ...styles.totalVal, color: "var(--c-text-muted)", fontWeight: 700 }}>
                    {retailReferenceSubtotal.toFixed(2)} сом
                  </span>
                </div>
              )}
              <div style={{ ...styles.totalRow, borderTop: "1px solid var(--c-border)", paddingTop: 12, marginTop: 4 }}>
                <span style={styles.totalLabelBig}>Итого</span>
                <span style={styles.totalBig}>{Number(sale.grand_total || 0).toFixed(2)} сом</span>
              </div>
              {isConfirmed && (
                <>
                  <div style={styles.totalRow}>
                    <span style={styles.totalLabel}>Оплачено</span>
                    <span style={{ ...styles.totalVal, color: "var(--c-success)" }}>
                      {Number(sale.paid_amount || 0).toFixed(2)} сом
                    </span>
                  </div>
                  {debt > 0 && (
                    <div style={styles.totalRow}>
                      <span style={styles.totalLabel}>Долг</span>
                      <span style={{ ...styles.totalVal, color: "var(--c-danger)", fontWeight: 900 }}>
                        {debt.toFixed(2)} сом
                      </span>
                    </div>
                  )}
                  <div style={{ ...styles.totalRow, marginTop: 4, paddingTop: 8, borderTop: "1px solid var(--c-border)" }}>
                    <span style={styles.totalLabel}>Общий долг клиента</span>
                    <span style={{ ...styles.totalVal, color: "var(--c-danger)", fontWeight: 900 }}>
                      {Number(sale.customer?.total_debt || 0).toFixed(2)} сом
                    </span>
                  </div>
                </>
              )}
            </div>

            <div style={styles.totalsActions}>
              {isDraft && items.length > 0 && (
                <button
                  type="button"
                  style={styles.confirmSaleBtn}
                  onClick={() => {
                    setPaymentMode("paid");
                    setPaymentType("cash");
                    setInitialPayment("");
                    setConfirmError(null);
                    setConfirmModal(true);
                  }}
                >
                  Подтвердить продажу
                </button>
              )}
              {isConfirmed && debt > 0 && (
                <button
                  type="button"
                  style={styles.repayBtn}
                  onClick={() => {
                    setRepayAmount("");
                    setRepayType("cash");
                    setRepayNotes("");
                    setRepayError(null);
                    setRepayModal(true);
                  }}
                >
                  Погасить долг
                </button>
              )}
            </div>
          </div>

          {payments.length > 0 && (
            <div style={styles.section}>
              <div style={styles.sectionTitle}>Платежи</div>
              <div style={styles.tableWrap}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>Дата</th>
                      <th style={styles.th}>Сумма</th>
                      <th style={styles.th}>Тип</th>
                      <th style={styles.th}>Заметки</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.map((p) => (
                      <tr key={p.id} style={styles.trStatic}>
                        <td style={styles.td}>
                          {p.payment_dt ? new Date(p.payment_dt).toLocaleString("ru-RU") : "—"}
                        </td>
                        <td style={styles.td}>
                          <span style={{ color: "var(--c-success)", fontWeight: 900 }}>
                            {Number(p.amount || 0).toFixed(2)} сом
                          </span>
                        </td>
                        <td style={styles.td}>
                          {{ cash: "Наличные", card: "Карта", online: "Перевод" }[p.payment_type] || p.payment_type}
                        </td>
                        <td style={styles.td}>{p.notes || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {confirmModal && (
        <div style={styles.overlay} onMouseDown={() => !confirmLoading && setConfirmModal(false)}>
          <div style={styles.modal} onMouseDown={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div style={styles.modalTitle}>Подтверждение продажи</div>
              <button
                type="button"
                onClick={() => !confirmLoading && setConfirmModal(false)}
                style={styles.modalClose}
              >
                ×
              </button>
            </div>
            <div style={styles.modalBody}>
              {confirmError && <div style={styles.error}>{confirmError}</div>}
              <div style={styles.infoBox}>
                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>Итого к оплате:</span>
                  <span style={{ ...styles.infoValue, color: "var(--c-accent)", fontSize: 20 }}>
                    {Number(sale?.grand_total || 0).toFixed(2)} сом
                  </span>
                </div>
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Вариант оплаты</label>
                <div style={styles.radioGroup}>
                  <label style={styles.radioLabel}>
                    <input
                      type="radio"
                      value="paid"
                      checked={paymentMode === "paid"}
                      onChange={() => setPaymentMode("paid")}
                      style={{ marginRight: 8 }}
                    />
                    Оплачено сразу (полная сумма)
                  </label>
                  <label style={styles.radioLabel}>
                    <input
                      type="radio"
                      value="credit"
                      checked={paymentMode === "credit"}
                      onChange={() => setPaymentMode("credit")}
                      style={{ marginRight: 8 }}
                    />
                    В долг (можно с частичной оплатой)
                  </label>
                </div>
              </div>
              {paymentMode === "credit" && (
                <div style={styles.field}>
                  <label style={styles.label}>Первоначальный платеж (0 = полный долг)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={initialPayment}
                    onChange={(e) => setInitialPayment(e.target.value)}
                    placeholder="0.00"
                    style={styles.input}
                  />
                </div>
              )}
              <div style={styles.field}>
                <label style={styles.label}>Способ оплаты</label>
                <Select
                  value={paymentType}
                  onChange={setPaymentType}
                  options={[
                    { value: "cash", label: "Наличные" },
                    { value: "card", label: "Карта" },
                    { value: "online", label: "Перевод" },
                  ]}
                  isSearchable={false}
                />
              </div>
              <div style={styles.modalActions}>
                <button type="button" style={styles.confirmBtn} onClick={handleConfirm} disabled={confirmLoading}>
                  {confirmLoading ? "Подтверждение..." : "Подтвердить"}
                </button>
                <button
                  type="button"
                  style={styles.cancelBtn}
                  onClick={() => setConfirmModal(false)}
                  disabled={confirmLoading}
                >
                  Отмена
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {repayModal && (
        <div style={styles.overlay} onMouseDown={() => !repayLoading && setRepayModal(false)}>
          <div style={styles.modal} onMouseDown={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div style={styles.modalTitle}>Погашение долга</div>
              <button type="button" onClick={() => !repayLoading && setRepayModal(false)} style={styles.modalClose}>
                ×
              </button>
            </div>
            <div style={styles.modalBody}>
              {repayError && <div style={styles.error}>{repayError}</div>}
              <div style={styles.infoBox}>
                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>Остаток долга:</span>
                  <span style={{ ...styles.infoValue, color: "var(--c-danger)", fontWeight: 900, fontSize: 19 }}>
                    {debt.toFixed(2)} сом
                  </span>
                </div>
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Сумма оплаты *</label>
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={repayAmount}
                  onChange={(e) => setRepayAmount(e.target.value)}
                  placeholder="0.00"
                  style={styles.input}
                  autoFocus
                />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Тип оплаты</label>
                <Select
                  value={repayType}
                  onChange={setRepayType}
                  options={[
                    { value: "cash", label: "Наличные" },
                    { value: "card", label: "Карта" },
                    { value: "online", label: "Перевод" },
                  ]}
                  isSearchable={false}
                />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Заметки</label>
                <input
                  type="text"
                  value={repayNotes}
                  onChange={(e) => setRepayNotes(e.target.value)}
                  placeholder="Необязательно"
                  style={styles.input}
                />
              </div>
              <div style={styles.modalActions}>
                <button type="button" style={styles.confirmBtn} onClick={handleRepay} disabled={repayLoading}>
                  {repayLoading ? "Сохранение..." : "Зафиксировать оплату"}
                </button>
                <button
                  type="button"
                  style={styles.cancelBtn}
                  onClick={() => setRepayModal(false)}
                  disabled={repayLoading}
                >
                  Отмена
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  page: {
    padding: "20px 28px",
    background: "var(--c-bg)",
    minHeight: "100%",
    color: "var(--c-text)",
    display: "flex",
    flexDirection: "column",
    gap: 20,
  },
  topBar: { display: "flex", alignItems: "center" },
  backBtn: {
    padding: "8px 16px",
    borderRadius: 99,
    border: "1px solid var(--c-border)",
    background: "var(--c-card)",
    color: "var(--c-text)",
    cursor: "pointer",
    fontWeight: 700,
    fontSize: 15,
  },
  notice: { padding: 16, textAlign: "center", opacity: 0.75, fontSize: 16 },
  error: {
    padding: "12px 16px",
    background: "var(--c-danger-dim)",
    border: "1px solid var(--c-danger)",
    borderRadius: 12,
    color: "var(--c-danger)",
    fontSize: 15,
  },
  errorInline: {
    padding: "8px 12px",
    background: "var(--c-danger-dim)",
    border: "1px solid var(--c-danger)",
    borderRadius: 10,
    color: "var(--c-danger)",
    fontSize: 14,
  },
  saleHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    flexWrap: "wrap",
    gap: 16,
    padding: "20px 24px",
    background: "var(--c-card)",
    border: "1px solid var(--c-border)",
    borderRadius: 16,
  },
  saleHeaderLeft: { display: "flex", flexDirection: "column", gap: 4 },
  saleTitle: { fontSize: 23, fontWeight: 900, color: "var(--c-text)" },
  saleMeta: { fontSize: 15, opacity: 0.75 },
  saleHeaderRight: { display: "flex", flexDirection: "column", gap: 8, alignItems: "flex-end" },
  badge: {
    display: "inline-block",
    padding: "4px 12px",
    borderRadius: 99,
    fontSize: 14,
    fontWeight: 900,
    border: "1px solid",
  },
  exportRow: { display: "flex", gap: 8 },
  exportBtn: {
    padding: "6px 14px",
    borderRadius: 99,
    border: "1px solid var(--c-border)",
    background: "var(--c-accent)",
    color: "var(--c-accent-on)",
    cursor: "pointer",
    fontWeight: 900,
    fontSize: 14,
    whiteSpace: "nowrap",
  },
  // ── POS search bar ─────────────────────────────────────────────────────────
  posSection: { display: "flex", flexDirection: "column", gap: 8 },
  posScanWrap: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    flexWrap: "wrap",
  },
  posIcon: {
    fontSize: 25,
    lineHeight: 1,
    opacity: 0.75,
    userSelect: "none",
  },
  posInputWrap: {
    position: "relative",
    flex: 1,
    minWidth: 220,
  },
  posInput: {
    width: "100%",
    padding: "12px 42px 12px 16px",
    borderRadius: 14,
    border: "2px solid var(--c-border)",
    background: "rgba(245,197,24,0.06)",
    color: "var(--c-text)",
    outline: "none",
    fontSize: 17,
    fontWeight: 600,
    letterSpacing: 0.2,
    boxSizing: "border-box",
    transition: "border-color 0.15s",
  },
  posSpinner: {
    position: "absolute",
    right: 14,
    top: "50%",
    transform: "translateY(-50%)",
    fontSize: 20,
    opacity: 0.7,
    animation: "spin 0.8s linear infinite",
    pointerEvents: "none",
  },
  dropdown: {
    position: "absolute",
    top: "calc(100% + 6px)",
    left: 0,
    right: 0,
    zIndex: 300,
    background: "var(--c-card)",
    border: "1px solid var(--c-border)",
    borderRadius: 16,
    boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
    overflow: "hidden",
    maxHeight: 360,
    overflowY: "auto",
  },
  dropdownEmpty: {
    position: "absolute",
    top: "calc(100% + 6px)",
    left: 0,
    right: 0,
    zIndex: 300,
    background: "var(--c-card)",
    border: "1px solid var(--c-border)",
    borderRadius: 16,
    padding: "14px 16px",
    fontSize: 15,
    opacity: 0.6,
    textAlign: "center",
  },
  dropdownItem: {
    padding: "10px 16px",
    cursor: "pointer",
    borderBottom: "1px solid var(--c-border)",
    transition: "background 0.1s",
  },
  dropdownItemActive: {
    padding: "10px 16px",
    cursor: "pointer",
    borderBottom: "1px solid var(--c-border)",
    background: "var(--c-card-hover)",
  },
  dropdownName: {
    fontSize: 16,
    fontWeight: 700,
    color: "var(--c-text)",
    marginBottom: 3,
  },
  dropdownMeta: {
    display: "flex",
    gap: 12,
    alignItems: "center",
    fontSize: 14,
    opacity: 0.75,
  },
  dropdownBarcode: { fontFamily: "monospace", letterSpacing: 0.5 },
  dropdownPrice: { color: "var(--c-success)", fontWeight: 700 },
  dropdownRetailHint: { color: "var(--c-text-muted)", fontWeight: 600 },
  dropdownInCart: {
    marginLeft: "auto",
    color: "var(--c-accent-on)",
    fontWeight: 900,
    fontSize: 13,
    background: "var(--c-accent)",
    padding: "2px 7px",
    borderRadius: 6,
  },
  addedToast: {
    display: "flex",
    alignItems: "center",
    gap: 7,
    padding: "9px 16px",
    background: "color-mix(in srgb, var(--c-success) 14%, transparent)",
    border: "1px solid rgba(34,197,94,0.40)",
    borderRadius: 12,
    color: "var(--c-success)",
    fontWeight: 800,
    fontSize: 16,
    whiteSpace: "nowrap",
    animation: "fadeIn 0.2s ease",
  },
  toastIcon: { fontSize: 20 },
  toastQty: {
    background: "rgba(34,197,94,0.28)",
    padding: "2px 9px",
    borderRadius: 8,
    fontWeight: 900,
    fontSize: 17,
  },
  toastHint: { fontSize: 14, opacity: 0.75 },
  // ── POS qty control ─────────────────────────────────────────────────────────
  qtyControl: {
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  qtyBtn: {
    width: 28,
    height: 28,
    borderRadius: 99,
    border: "1px solid var(--c-border)",
    background: "var(--c-card)",
    color: "var(--c-text)",
    cursor: "pointer",
    fontWeight: 900,
    fontSize: 18,
    lineHeight: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  qtyValue: {
    minWidth: 36,
    textAlign: "center",
    fontWeight: 700,
    fontFamily: "monospace",
    fontSize: 16,
  },
  section: { display: "flex", flexDirection: "column", gap: 12 },
  sectionHeading: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
  },
  sectionTitle: { fontSize: 18, fontWeight: 900, color: "var(--c-text)" },
  retailToggle: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 15,
    fontWeight: 600,
    cursor: "pointer",
    userSelect: "none",
    opacity: 0.9,
  },
  empty: { padding: 32, textAlign: "center", opacity: 0.65, fontSize: 15 },
  tableWrap: {
    border: "1px solid var(--c-border)",
    borderRadius: 16,
    overflow: "hidden",
    background: "var(--c-card)",
  },
  table: { width: "100%", borderCollapse: "separate", borderSpacing: 0 },
  th: {
    padding: "12px 14px",
    fontSize: 14,
    fontWeight: 900,
    color: "var(--c-text-sub)",
    background: "var(--c-input)",
    borderBottom: "1px solid var(--c-border)",
    textAlign: "left",
    whiteSpace: "nowrap",
  },
  trStatic: {},
  trStockWarn: {
    background: "var(--c-danger-dim)",
    boxShadow: "inset 3px 0 0 var(--c-danger)",
  },
  trStockWarnEditing: {
    outline: "1px solid rgba(245,197,24,0.35)",
  },
  stockWarnMsg: {
    marginTop: 6,
    fontSize: 14,
    fontWeight: 700,
    color: "var(--c-danger)",
    lineHeight: 1.35,
  },
  td: {
    padding: "11px 14px",
    fontSize: 15,
    borderBottom: "1px solid var(--c-border)",
    verticalAlign: "middle",
  },
  tdMono: {
    padding: "11px 14px",
    fontSize: 15,
    borderBottom: "1px solid var(--c-border)",
    fontFamily: "monospace",
  },
  cellInput: {
    width: 90,
    padding: "5px 8px",
    borderRadius: 8,
    border: "1px solid var(--c-border)",
    background: "var(--c-card-hover)",
    color: "var(--c-text)",
    outline: "none",
    fontSize: 15,
    textAlign: "center",
    fontFamily: "monospace",
  },
  rowActions: { display: "flex", gap: 6 },
  saveBtn: {
    padding: "5px 10px",
    borderRadius: 99,
    border: "1px solid color-mix(in srgb, var(--c-success) 30%, transparent)",
    background: "color-mix(in srgb, var(--c-success) 14%, transparent)",
    color: "var(--c-success)",
    cursor: "pointer",
    fontWeight: 900,
    fontSize: 15,
  },
  cancelSmallBtn: {
    padding: "5px 10px",
    borderRadius: 99,
    border: "1px solid var(--c-border)",
    background: "var(--c-card)",
    color: "var(--c-text)",
    cursor: "pointer",
    fontWeight: 900,
    fontSize: 15,
  },
  editBtn: {
    padding: "5px 10px",
    borderRadius: 99,
    border: "1px solid var(--c-border)",
    background: "var(--c-accent)",
    color: "var(--c-accent-on)",
    cursor: "pointer",
    fontWeight: 900,
    fontSize: 13,
  },
  removeBtn: {
    padding: "5px 10px",
    borderRadius: 99,
    border: "1px solid rgba(251,113,133,0.35)",
    background: "rgba(251,113,133,0.12)",
    color: "var(--c-danger)",
    cursor: "pointer",
    fontWeight: 900,
    fontSize: 13,
  },
  discountSection: { display: "flex", flexDirection: "column", gap: 12 },
  discountRow: { display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" },
  discountSelect: {
    padding: "10px 14px",
    borderRadius: 10,
    border: "1px solid var(--c-border)",
    background: "var(--c-card)",
    color: "var(--c-text)",
    outline: "none",
    fontSize: 16,
    cursor: "pointer",
    minWidth: 200,
  },
  discountInput: {
    width: 140,
    padding: "10px 14px",
    borderRadius: 10,
    border: "1px solid var(--c-border)",
    background: "var(--c-card)",
    color: "var(--c-text)",
    outline: "none",
    fontSize: 16,
    textAlign: "center",
    fontFamily: "monospace",
  },
  saveDiscountBtn: {
    padding: "10px 20px",
    borderRadius: 99,
    border: "1px solid var(--c-border)",
    background: "var(--c-accent)",
    color: "var(--c-accent-on)",
    cursor: "pointer",
    fontWeight: 900,
    fontSize: 15,
    whiteSpace: "nowrap",
  },
  totalsCard: {
    padding: "24px 28px",
    background: "var(--c-card)",
    border: "1px solid var(--c-border)",
    borderRadius: 16,
    display: "flex",
    flexDirection: "column",
    gap: 20,
  },
  totalsGrid: { display: "flex", flexDirection: "column", gap: 10, maxWidth: 400 },
  totalRow: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 20 },
  totalLabel: { fontSize: 16, opacity: 0.80, fontWeight: 700 },
  totalVal: { fontSize: 16, fontWeight: 900 },
  totalLabelBig: { fontSize: 18, fontWeight: 900 },
  totalBig: { fontSize: 23, fontWeight: 900, color: "var(--c-text)" },
  totalsActions: { display: "flex", gap: 12, flexWrap: "wrap" },
  confirmSaleBtn: {
    padding: "14px 28px",
    borderRadius: 99,
    border: "1px solid color-mix(in srgb, var(--c-success) 30%, transparent)",
    background: "color-mix(in srgb, var(--c-success) 14%, transparent)",
    color: "var(--c-success)",
    cursor: "pointer",
    fontWeight: 900,
    fontSize: 17,
    whiteSpace: "nowrap",
  },
  repayBtn: {
    padding: "14px 28px",
    borderRadius: 99,
    border: "1px solid rgba(245,197,24,0.3)",
    background: "var(--c-accent)",
    color: "var(--c-accent-on)",
    cursor: "pointer",
    fontWeight: 900,
    fontSize: 17,
    whiteSpace: "nowrap",
  },
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.55)",
    backdropFilter: "blur(8px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 16,
    zIndex: 50,
  },
  modal: {
    width: "min(460px, 90vw)",
    maxHeight: "90vh",
    background: "var(--c-bg)",
    border: "1px solid var(--c-border)",
    borderRadius: 16,
    boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  modalHeader: {
    padding: "16px 20px",
    borderBottom: "1px solid var(--c-border)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  modalTitle: { fontSize: 18, fontWeight: 900, color: "var(--c-text)" },
  modalClose: {
    width: 34,
    height: 34,
    borderRadius: 16,
    border: "1px solid var(--c-border)",
    background: "var(--c-card)",
    color: "var(--c-text)",
    cursor: "pointer",
    fontWeight: 900,
    fontSize: 20,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  modalBody: {
    padding: 20,
    overflow: "auto",
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: 16,
  },
  infoBox: {
    padding: "14px 16px",
    background: "var(--c-card)",
    border: "1px solid var(--c-border)",
    borderRadius: 16,
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  infoRow: { display: "flex", justifyContent: "space-between", fontSize: 15 },
  infoLabel: { opacity: 0.75, fontWeight: 700 },
  infoValue: { fontWeight: 900 },
  field: { display: "flex", flexDirection: "column", gap: 6 },
  label: { fontSize: 15, fontWeight: 700, color: "var(--c-text-sub)" },
  input: {
    padding: "10px 14px",
    borderRadius: 10,
    border: "1px solid var(--c-border)",
    background: "var(--c-card)",
    color: "var(--c-text)",
    outline: "none",
    fontSize: 16,
  },
  select: {
    padding: "10px 14px",
    borderRadius: 10,
    border: "1px solid var(--c-border)",
    background: "var(--c-card)",
    color: "var(--c-text)",
    outline: "none",
    fontSize: 16,
    cursor: "pointer",
  },
  radioGroup: { display: "flex", flexDirection: "column", gap: 10 },
  radioLabel: { fontSize: 16, cursor: "pointer", display: "flex", alignItems: "center" },
  modalActions: { display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 4 },
  confirmBtn: {
    padding: "11px 22px",
    borderRadius: 99,
    border: "1px solid color-mix(in srgb, var(--c-success) 30%, transparent)",
    background: "color-mix(in srgb, var(--c-success) 14%, transparent)",
    color: "var(--c-success)",
    cursor: "pointer",
    fontWeight: 900,
    fontSize: 16,
  },
  cancelBtn: {
    padding: "11px 22px",
    borderRadius: 99,
    border: "1px solid var(--c-border)",
    background: "var(--c-card)",
    color: "var(--c-text)",
    cursor: "pointer",
    fontWeight: 900,
    fontSize: 16,
  },
};
